"use client";

import { useCallback, useEffect, useState } from "react";
import { apiUrl } from "@/lib/aicheck/api-base";
import { authService } from "@/services/auth";

interface AICheckLocalIdentity {
  email: string | null;
  roles: string[];
}

export type AICheckLocalIdentityStatus =
  | "loading"
  | "anonymous"
  | "viewer"
  | "knowledge"
  | "admin";

export function useAICheckLocalIdentity(): {
  identity: AICheckLocalIdentity | null;
  status: AICheckLocalIdentityStatus;
  canViewKnowledge: boolean;
  canAdminKnowledge: boolean;
  reload: () => void;
} {
  const [identity, setIdentity] = useState<AICheckLocalIdentity | null>(null);
  const [status, setStatus] = useState<AICheckLocalIdentityStatus>("loading");
  const [version, setVersion] = useState(0);

  useEffect(() => {
    void version;
    let cancelled = false;
    async function load() {
      setStatus("loading");
      const token = await authService.getIdToken();
      if (!token) {
        if (!cancelled) setStatus("anonymous");
        return;
      }
      const response = await fetch(apiUrl("/api/aicheck/me"), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        if (!cancelled) setStatus("anonymous");
        return;
      }
      const data = (await response.json()) as AICheckLocalIdentity;
      if (cancelled) return;
      setIdentity(data);
      if (data.roles.includes("aicheck_admin")) setStatus("admin");
      else if (data.roles.includes("knowledge_admin")) setStatus("knowledge");
      else if (data.roles.includes("knowledge_reviewer")) setStatus("viewer");
      else setStatus("anonymous");
    }
    load().catch(() => {
      if (!cancelled) setStatus("anonymous");
    });
    return () => {
      cancelled = true;
    };
  }, [version]);

  const roles = identity?.roles ?? [];
  const canAdminKnowledge =
    roles.includes("aicheck_admin") || roles.includes("knowledge_admin");
  const canViewKnowledge =
    canAdminKnowledge || roles.includes("knowledge_reviewer");

  // Stable reference — safe to use as a useEffect dependency in consumers.
  const reload = useCallback(() => setVersion((current) => current + 1), []);

  return {
    identity,
    status,
    canViewKnowledge,
    canAdminKnowledge,
    reload,
  };
}
